// React
import React, { useEffect, useRef, useState } from "react";

// React Router
import { useLocation } from "react-router-dom";

// MUI Components
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

// MUI Icons
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import EthernetIcon from "@mui/icons-material/Cable";
import ComputerIcon from "@mui/icons-material/Computer";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DevicesIcon from "@mui/icons-material/Devices";
import ModeIcon from "@mui/icons-material/Mode";
import GSMIcon from "@mui/icons-material/NetworkCell";
import WifiIcon from "@mui/icons-material/NetworkWifi";
import SignalCellular1BarIcon from "@mui/icons-material/SignalCellular1Bar";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SignalCellularAlt2BarIcon from "@mui/icons-material/SignalCellularAlt2Bar";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import Battery90Icon from "@mui/icons-material/Battery90";
import Battery80Icon from "@mui/icons-material/Battery80";
import Battery60Icon from "@mui/icons-material/Battery60";
import Battery50Icon from "@mui/icons-material/Battery50";
import Battery30Icon from "@mui/icons-material/Battery30";
import Battery20Icon from "@mui/icons-material/Battery20";
import Battery0BarIcon from "@mui/icons-material/Battery0Bar";

// i18n
import { useTranslation } from "react-i18next";

// External Libraries
import { v4 as uuidv4 } from "uuid";

// API
import { postCommand } from "../../../api/commandAPI";
import { getDevices, isNodeDeviceOnline } from "../../../api/deviceAPI";
import { getProjectDetailStatuses } from "../../../api/projectAPI";
import postStatus from "../../../api/statusAPI";

// Utils & Config
import { useRefreshInterval } from "../../../contexts/RefreshIntervalContext";
import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { ConvertOIDValueToString } from "../../../utils/objectConverter";
import { getPlaceIdFromPathname } from "../../../utils/pathUtils";
import { useObjectData } from "../../../hooks/useLocalStorageData";
import { useSnackbar } from "../../../hooks/useSnackbar";
import { getCurrentTenant } from "../../../config/tenantConfig";

function getIccidOperator(iccid) {
  if (iccid == null) return null;
  // "SIM 1 ICCID: 8990011615060009996" gibi prefix'li değerleri destekle:
  // metindeki en uzun rakam dizisini ICCID olarak al
  const runs = String(iccid).match(/\d+/g) || [];
  if (!runs.length) return null;
  const digits = runs.reduce((a, b) => (b.length > a.length ? b : a), "");

  if (!digits.startsWith("8990")) return "Yabancı / Bilinmeyen";
  if (digits.startsWith("899001") || digits.startsWith("899028601"))
    return "Turkcell";
  if (digits.startsWith("899002") || digits.startsWith("899028602"))
    return "Vodafone";
  if (digits.startsWith("899003") || digits.startsWith("899028603"))
    return "Türk Telekom";
  return "Bilinmeyen";
}

// Pick the battery icon that best matches the level (%).
function getBatteryIcon(v) {
  if (v >= 95) return BatteryFullIcon;
  if (v >= 90) return Battery90Icon;
  if (v >= 80) return Battery80Icon;
  if (v >= 60) return Battery60Icon;
  if (v >= 50) return Battery50Icon;
  if (v >= 30) return Battery30Icon;
  if (v >= 20) return Battery20Icon;
  return Battery0BarIcon;
}

export default function NodeTag() {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const [groupedData, setGroupedData] = useState([]);
  const [onlineStatus, setOnlineStatus] = useState({});
  const [summaryDetailOrder, setSummaryDetailOrder] = useState([]);
  const { interval, isRunning } = useRefreshInterval();
  const isPageVisibility = usePageVisibility();
  // Use custom hook to get objectData from localStorage
  const objectData = useObjectData();
  const isRcu = getCurrentTenant().projectCode === "RCU";

  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? parseInt(rawId, 10) : null;

  const latestNodeId = useRef(node_id);
  const latestObjectData = useRef(objectData);
  const commandKeyRef = useRef(uuidv4());
  const cmdStr = "updateStatus";
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [dialogOpenForId, setDialogOpenForId] = useState(null);

  // Update refs on every render
  latestNodeId.current = node_id;
  latestObjectData.current = objectData;

  useEffect(() => {
    (async () => {
      try {
        const response = await getProjectDetailStatuses();
        // Axios interceptor returns response.data.data if returnCode === 0, otherwise returns full response
        const result = Array.isArray(response) ? response : response?.data;
        // API returns an array of codes as strings
        if (
          Array.isArray(result) &&
          result.every((x) => typeof x === "string")
        ) {
          setSummaryDetailOrder(result);
        }
      } catch (e) {
        console.error("getProjectDetailStatuses API call failed:", e);
      }
    })();
  }, []);

  const handleOpenDialog = (id) => setDialogOpenForId(id);
  const handleCloseDialog = () => setDialogOpenForId(null);

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text.toString());
      const ipv4Regex = /^\d{1,3}(?:\.\d{1,3}){3}$/;
      const isIp = ipv4Regex.test(text);
      showSnackbar(
        isIp ? "IP adresi panoya kopyalandı" : "Değer panoya kopyalandı",
        "success"
      );
    } catch (err) {
      showSnackbar("Panoya kopyalama başarısız", "error");
    }
  };

  const initialRequestsSent = useRef(false);

  useEffect(() => {
    let intervalId = null;
    let commandTimeoutId = null;

    async function fetchStatus(currentNodeId, currentObjectData) {
      const baseAllowedObjectCodes = [
        "2",
        "3",
        "4",
        "6",
        "7",
        "8",
        "12",
        "15",
        "16",
        "17",
        "18",
        "19",
        "24",
        "67",
        "68",
        "69",
        "70",
      ];
      const allowedObjectCodes = Array.from(
        new Set([
          ...baseAllowedObjectCodes,
          ...summaryDetailOrder,
          // RCU: battery level (OID 3002) comes directly from /api/Status.
          ...(isRcu ? ["3002"] : []),
        ])
      );

      const nodeList = currentNodeId ? [currentNodeId] : null;
      const response = await postStatus({
        objectCodeList: allowedObjectCodes,
        nodeList,
      });
      const result = Array.isArray(response) ? response : response?.data;

      if (result && Array.isArray(result)) {
        const filteredByObjectCode = result;
        const groupByDType = (data) => {
          const groups = {};
          data.forEach((item) => {
            if (!groups[item.dType]) {
              groups[item.dType] = [];
            }
            groups[item.dType].push(item);
          });

          const result = Object.values(groups).map((group) => {
            const pairsObject = {};
            group.forEach((item) => {
              pairsObject[item.objectCode] = item.value;
            });
            return pairsObject;
          });

          return result;
        };

        const grouped = groupByDType(filteredByObjectCode);
        const processedData = grouped.map((dataObj, index) => {
          const processedObj = {};
          processedObj.id = index + 1;

          for (const [objectCode, value] of Object.entries(dataObj)) {
            const objectDef = currentObjectData.find(
              (item) => item.code === parseInt(objectCode, 10)
            );

            if (objectDef) {
              const objectOID = { Type: objectDef.type };
              const convertedValue = ConvertOIDValueToString(objectOID, value);
              processedObj[objectCode] = {
                value: convertedValue,
                name: objectDef.name,
                nonConvertedValue: value,
              };
            } else {
              processedObj[objectCode] = {
                value: value,
                name: "UNKNOWN",
                nonConvertedValue: value,
              };
            }
          }
          return processedObj;
        });
        setGroupedData(processedData);
        async function fetchOnlineStatus(devices) {
          const statusObj = {};
          await Promise.all(
            devices.map(async (device) => {
              const serial = device["2"]?.value;
              if (serial) {
                try {
                  const response = await isNodeDeviceOnline(serial);
                  const isOnline = typeof response === 'boolean' ? response : response?.data;
                  statusObj[serial] = isOnline;
                } catch (e) {
                  console.error("[NodeTag] Error fetching online status for serial:", serial, e);
                  statusObj[serial] = false;
                }
              }
            })
          );
          setOnlineStatus(statusObj);
        }
        fetchOnlineStatus(processedData);
      }
    }

    if (!initialRequestsSent.current) {
      fetchStatus(latestNodeId.current, latestObjectData.current);
      initialRequestsSent.current = true;
    }

    if (isRunning && isPageVisibility) {
      const commandTimeoutMs = Math.max((interval - 5) * 1000, 0);

      // İlk komut gönderimi
      commandTimeoutId = setTimeout(() => {
        sendCommandToDevice(node_id, commandKeyRef.current, cmdStr);
      }, commandTimeoutMs);

      intervalId = setInterval(() => {
        fetchStatus(latestNodeId.current, latestObjectData.current);

        // Eski timeout'u temizle
        if (commandTimeoutId) {
          clearTimeout(commandTimeoutId);
        }

        const commandTimeoutMsInner = Math.max((interval - 5) * 1000, 0);
        commandTimeoutId = setTimeout(() => {
          sendCommandToDevice(node_id, commandKeyRef.current, cmdStr);
        }, commandTimeoutMsInner);
      }, interval * 1000);
    }

    async function sendCommandToDevice(nodeId, cmdKey, cmdStr) {
      try {
        const devicesResponse = await getDevices();
        const devices = Array.isArray(devicesResponse) ? devicesResponse : devicesResponse?.data;

        if (devices && Array.isArray(devices)) {
          const filteredDevices = devices.filter(
            (device) => device.node_idNode === nodeId
          );
          const targetDevice = filteredDevices[0];
          if (targetDevice) {
            await postCommand(nodeId, targetDevice.idDevice, cmdKey, cmdStr, []);
          }
        }
      } catch (error) {
        console.error("[NodeTag] Error sending command to device:", error);
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (commandTimeoutId) clearTimeout(commandTimeoutId);
    };
  }, [node_id, interval, isRunning, isPageVisibility, summaryDetailOrder, isRcu]);

  return (
    <Grid container spacing={2} columns={12}>
      {groupedData.map((device) => {
        let ConnectionIcon;
        if (device["18"]?.value === "WIFI") {
          ConnectionIcon = WifiIcon;
        } else if (device["18"]?.value === "GSM") {
          ConnectionIcon = GSMIcon;
        } else {
          ConnectionIcon = EthernetIcon;
        }

        const levelVal = device["15"]?.value;
        let LevelIcon;
        if (levelVal != null) {
          if (levelVal < 35) {
            LevelIcon = SignalCellular1BarIcon;
          } else if (levelVal < 70) {
            LevelIcon = SignalCellularAlt2BarIcon;
          } else {
            LevelIcon = SignalCellularAltIcon;
          }
        }

        const levelVall = device["12"]?.value;
        let LevelIconn;
        if (levelVall != null) {
          if (levelVall < 35) {
            LevelIconn = SignalCellular1BarIcon;
          } else if (levelVall < 70) {
            LevelIconn = SignalCellularAlt2BarIcon;
          } else {
            LevelIconn = SignalCellularAltIcon;
          }
        }

        const serial = device["2"]?.value;
        const isOnline = serial ? onlineStatus[serial] : false;

        // RCU: battery (OID 3002) comes directly from /api/Status — shown when present.
        const batteryRaw =
          device["3002"]?.nonConvertedValue ?? device["3002"]?.value;
        const hasBattery =
          batteryRaw != null && !Number.isNaN(Number(batteryRaw));
        const batteryNum = hasBattery ? Number(batteryRaw) : 0;
        const batteryColor =
          batteryNum >= 60 ? "#2e7d32" : batteryNum >= 20 ? "#ed6c02" : "#d32f2f";
        const BatteryIcon = getBatteryIcon(batteryNum);
        const connectionDetail =
          device["18"]?.value === "GSM"
            ? device["68"]?.value ?? "-"
            : device["18"]?.value === "WIFI"
            ? device["67"]?.value ?? "-"
            : device["69"]?.value ?? "-";
        const result = (
          <React.Fragment key={device.id}>
            <Grid size={{ xs: 12, sm: 6, lg: 12 }}>
              <Card sx={{ height: "100%", border: "2px solid #e0e0e0" }}>
                <CardContent>
                  {" "}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={3}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="flex-start"
                    flexWrap="wrap"
                    sx={{ gap: 2 }}
                  >
                    <Stack
                      alignItems="center"
                      spacing={1}
                      sx={{ mr: { xs: 0, md: 2 }, mb: { xs: 2, md: 0 } }}
                    >
                      <Box
                        sx={{
                          bgcolor: isOnline ? "#4caf50" : "red",
                          color: "white",
                          p: 1,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "45px",
                        }}
                      >
                        <ComputerIcon />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.75rem",
                          textAlign: "center",
                        }}
                      >
                         Serial No<br />
                        {device["2"]?.value}
                      </Typography>
                    </Stack>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        display: { xs: "none", lg: "block" },
                        height: "72px",
                        mx: 1,
                        alignSelf: "center",
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Box
                      sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }}
                    />
                    {device["19"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <ModeIcon />
                            <Typography
                              variant="body2"
                              title={device["19"].value}
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.mode")}
                              </span>{" "}
                              {device["19"].value &&
                              device["19"].value.length > 8
                                ? device["19"].value.substring(0, 8) + "...."
                                : device["19"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["3"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <DevicesIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.type")}
                              </span>{" "}
                              {device["3"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["7"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <AccessTimeIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.uptime")}
                              </span>{" "}
                              {device["7"] && device["7"].value
                                ? device["7"].value
                                : t("nodeTag.na")}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["8"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <BrowserUpdatedIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.softwareUpdate")}
                              </span>{" "}
                              {device["8"] && device["8"].value
                                ? device["8"].value
                                : t("nodeTag.na")}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["18"]?.value != null && (
                      <Card
                        onClick={() => handleCopy(connectionDetail)}
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                          cursor: "pointer",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ pt: 0 }}
                          >
                            <ConnectionIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "bold",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("nodeTag.connection")} {device["18"].value}
                              </span>
                              <br />
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  color:
                                    theme &&
                                    theme.palette &&
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "#000",
                                }}
                              >
                                {connectionDetail}
                              </span>
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["15"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <LevelIcon />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.wifiLevel")}
                              </span>{" "}
                              {device["15"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}{" "}
                    {device["12"]?.value != null && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <LevelIconn />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.gsmLevel")}
                              </span>{" "}
                              {device["12"].value}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                    {isRcu && hasBattery && (
                      <Card
                        sx={{
                          width: { xs: "100%", sm: "150px" },
                          height: "72px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <BatteryIcon sx={{ color: batteryColor }} />
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                              }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {t("nodeTag.battery", {
                                  defaultValue: "Battery Level",
                                })}
                              </span>{" "}
                              %{Math.round(batteryNum)}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                    {/* IMEI/IMSI are shown only inside the "Detay" dialog, not
                        as cards in the main tag view. */}
                    <Box
                      sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }}
                    />
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        display: { xs: "none", lg: "block" },
                        height: "72px",
                        mx: 1,
                        alignSelf: "center",
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Box sx={{ ml: { md: "auto" } }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenDialog(device.id)}
                        sx={{
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: 2,
                          },
                        }}
                      >
                        {t("nodeTag.more", { defaultValue: "Detay" })}
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Dialog
              open={dialogOpenForId === device.id}
              onClose={handleCloseDialog}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>
                {t("nodeTag.dialogTitle", { defaultValue: "Cihaz Detayı" })}
              </DialogTitle>
              <DialogContent dividers>
                {/* Header chips */}
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mb: 2 }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ComputerIcon fontSize="small" />}
                    label={`Serial: ${device["2"]?.value ?? "-"}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ModeIcon fontSize="small" />}
                    label={`FirmWare / HardWare: ${
                      device["6"]?.value ?? "N/A"
                    } / ${device["4"]?.value ?? "N/A"}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ConnectionIcon fontSize="small" />}
                    label={`${t("nodeTag.connection", {
                      defaultValue: "Bağlantı",
                    })}: ${device["18"]?.value ?? "-"}`}
                  />
                  {device["70"]?.value && (
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<ModeIcon fontSize="small" />}
                      label={`MDB FW: ${device["70"].value}`}
                    />
                  )}
                </Stack>
                {/* Details list */}
                {(() => {
                  // RCU: IMEI (16) / IMSI (17) are rendered as their own full-value
                  // rows below, so exclude them from the generic details list.
                  const details = summaryDetailOrder.filter(
                    (code) =>
                      device?.[code]?.value != null &&
                      !(isRcu && (code === "16" || code === "17"))
                  );
                  const showImei = isRcu && device["16"]?.value != null;
                  const showImsi = isRcu && device["17"]?.value != null;
                  const hasIccid = device["24"]?.value != null;

                  if (
                    details.length === 0 &&
                    !showImei &&
                    !showImsi &&
                    !hasIccid
                  ) {
                    return (
                      <Typography variant="body2">
                        {t("nodeTag.noDetails", {
                          defaultValue: "Gösterilecek detay bulunamadı",
                        })}
                      </Typography>
                    );
                  }

                  return (
                    <List dense sx={{ py: 0 }}>
                      {device["24"]?.value && (
                        <ListItem
                          key="sim-iccid"
                          divider
                          disableGutters
                          secondaryAction={
                            <Tooltip
                              title={t("common.copy", {
                                defaultValue: "Kopyala",
                              })}
                            >
                              <IconButton
                                edge="end"
                                onClick={() => handleCopy(device["24"].value)}
                                size="small"
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemText
                            primary="SIM ICCID"
                            primaryTypographyProps={{
                              sx: { fontWeight: 600 },
                            }}
                            secondary={
                              <Typography
                                variant="body2"
                                title={String(device["24"].value)}
                                onClick={() => handleCopy(device["24"].value)}
                                sx={{
                                  cursor: "pointer",
                                  fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                }}
                              >
                                {device["24"].value}
                                {getIccidOperator(device["24"].value) && (
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{
                                      ml: 1,
                                      fontFamily: "inherit",
                                      color: "text.secondary",
                                    }}
                                  >
                                    / {getIccidOperator(device["24"].value)}
                                  </Typography>
                                )}
                              </Typography>
                            }
                          />
                        </ListItem>
                      )}
                      {showImei && (
                        <ListItem
                          key="imei"
                          divider
                          disableGutters
                          secondaryAction={
                            <Tooltip
                              title={t("common.copy", {
                                defaultValue: "Kopyala",
                              })}
                            >
                              <IconButton
                                edge="end"
                                onClick={() => handleCopy(device["16"].value)}
                                size="small"
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemText
                            primary="IMEI"
                            primaryTypographyProps={{
                              sx: { fontWeight: 600 },
                            }}
                            secondary={
                              <Typography
                                variant="body2"
                                title={String(device["16"].value)}
                                onClick={() => handleCopy(device["16"].value)}
                                sx={{
                                  cursor: "pointer",
                                  wordBreak: "break-all",
                                  fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                }}
                              >
                                {device["16"].value}
                              </Typography>
                            }
                          />
                        </ListItem>
                      )}
                      {showImsi && (
                        <ListItem
                          key="imsi"
                          divider
                          disableGutters
                          secondaryAction={
                            <Tooltip
                              title={t("common.copy", {
                                defaultValue: "Kopyala",
                              })}
                            >
                              <IconButton
                                edge="end"
                                onClick={() => handleCopy(device["17"].value)}
                                size="small"
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemText
                            primary="IMSI"
                            primaryTypographyProps={{
                              sx: { fontWeight: 600 },
                            }}
                            secondary={
                              <Typography
                                variant="body2"
                                title={String(device["17"].value)}
                                onClick={() => handleCopy(device["17"].value)}
                                sx={{
                                  cursor: "pointer",
                                  wordBreak: "break-all",
                                  fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                }}
                              >
                                {device["17"].value}
                              </Typography>
                            }
                          />
                        </ListItem>
                      )}
                      {details.map((code) => {
                        const label = device?.[code]?.name ?? code;
                        const value = device?.[code]?.value;
                        return (
                          <ListItem
                            key={code}
                            divider
                            disableGutters
                            secondaryAction={
                              <Tooltip
                                title={t("common.copy", {
                                  defaultValue: "Kopyala",
                                })}
                              >
                                <IconButton
                                  edge="end"
                                  onClick={() => handleCopy(value)}
                                  size="small"
                                >
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            }
                          >
                            <ListItemText
                              primary={label}
                              primaryTypographyProps={{
                                sx: { fontWeight: 600 },
                              }}
                              secondary={
                                <Typography
                                  variant="body2"
                                  title={String(value)}
                                  onClick={() => handleCopy(value)}
                                  sx={{
                                    cursor: "pointer",
                                    fontFamily:
                                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                  }}
                                >
                                  {value}
                                </Typography>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  );
                })()}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>
                  {t("common.close", { defaultValue: "Kapat" })}
                </Button>
              </DialogActions>
            </Dialog>
            <Snackbar
              key={snackbar.message}
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={hideSnackbar}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={hideSnackbar}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
                variant="filled"
                elevation={6}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </React.Fragment>
        );
        return result;
      })}
    </Grid>
  );
}
